import { useState } from "react";
import { useSetRecoilState } from "recoil";
import Table from "react-bootstrap/Table";
import Pagination from "react-bootstrap/Pagination";
import Form from "react-bootstrap/Form";
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  getFilteredRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  useReactTable,
} from "@tanstack/react-table";

import { icn3dUrlState } from "./visualize.state";

export default function VisualizeResultsTable({ results = [] }) {
  const [sorting, setSorting] = useState([{ id: "Variant", desc: false }]);
  const [columnFilters, setColumnFilters] = useState([]);
  const [active, setActive] = useState(results.findIndex(row => row?._mobileICn3Dlink?.includes("https")));

  const setIcn3dUrl = useSetRecoilState(icn3dUrlState);

  const columnNames = Object.keys(results?.[0] || []).filter((key) => key && !key.startsWith("_"));

  const positionSort = (a, b) => {
    const parse = (value) => parseInt(value.replace(/[A-Z]*/gi, "")) || 0;
    return parse(a.original.AminoAcidMutation) - parse(b.original.AminoAcidMutation);
  };

  const localeSort = (a, b) => {
    return a.original.Variant.localeCompare(b.original.Variant)
  }

  function getSortMethod(key){

    switch(key){
      case "AminoAcidMutation":
        return positionSort;
      case "Variant" :
        return localeSort;
      default:
        return "auto"
    }
  }

  const vizsnpColumns = columnNames.map((key) => ({
    Header: key,
    accessorKey: key,
    className: "text-nowrap align-middle",
    sortingFn: getSortMethod(key),
    cell: (e) =>
      e.getValue().toString().includes("https://www.ncbi.nlm.nih.gov/Structure/icn3d") ? (
        <a target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-dark text-decoration-none" href={e.getValue()}>
          View in iCn3D
        </a>
      ) :
      e.getValue().toString().includes("https://useast.ensembl.org/Homo_sapiens/Gene/Summary") ? (
        <a target="_blank" href={e.getValue()}>{e.getValue().split("=").pop()}</a>
      ) :
      e.getValue().toString().includes("https://www.uniprot.org/uniprotkb") ? (
        <a target="_blank" href={e.getValue()}>{e.getValue().split("/").pop()}</a>
      ) :
      e.getValue().toString().includes("https://www.rcsb.org/structure") ? (
        <a target="_blank" href={e.getValue()}>{e.getValue().split("/").pop()}</a>
      ) :
      (
        e.getValue()
      ),
  }));

  const table = useReactTable({
    data: results,
    columns: vizsnpColumns,
    state: {
      columnFilters,
      sorting,
    },
    sortingFns: {
      positionSort,
      localeSort,
    },
    onColumnFiltersChange: setColumnFilters,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  return (
    <>
      <Table striped hover responsive>
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <th key={header.id} colSpan={header.colSpan} className={header.column.columnDef.className}>
                  {header.isPlaceholder ? null : (
                    <>
                      <div className={header.column.getCanSort() ? "cursor-pointer" : ""} onClick={header.column.getToggleSortingHandler()}>
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {{
                          asc: <i className="bi bi-sort-up ms-1" />,
                          desc: <i className="bi bi-sort-down ms-1" />,
                        }[header.column.getIsSorted()] ?? null}
                      </div>
                      <div>
                        <Form.Control type="input" onChange={(e) => header.column.setFilterValue(e.target.value)} placeholder={"Filter..."} />
                      </div>
                    </>
                  )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr
              onClick={() => {
                if (row.original.iCn3Dlink?.toString().includes("https://www.ncbi.nlm.nih.gov/Structure/icn3d")) {
                  setActive(row.original._id);
                  setIcn3dUrl(row.original._mobileICn3Dlink);
                }
              }}
              className={active === row.original._id ? "active-row" : ""}
              key={row.original._id}
            >
              {row.getVisibleCells().map((cell) => (
                <td key={cell.id} className={cell.column.columnDef.className}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </Table>

      <div className="d-flex flex-wrap justify-content-between align-items-center">
        <small>
          Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
        </small>
        <div className="d-flex flex-wrap">
          <Form.Select
            size="sm"
            aria-label="table-pagination"
            className="w-auto me-1"
            value={table.getState().pagination.pageSize}
            onChange={(e) => {
              table.setPageSize(Number(e.target.value));
            }}
          >
            {[10, 25, 50, 100].map((pageSize) => (
              <option key={pageSize} value={pageSize}>
                Show {pageSize}
              </option>
            ))}
          </Form.Select>
          <Pagination className="mb-0" size="sm">
            <Pagination.First onClick={() => table.setPageIndex(0)} disabled={!table.getCanPreviousPage()}>
              <span className="d-none d-md-inline-block">First</span>
              <span className="d-inline-block d-md-none">&lt;&lt;</span>
            </Pagination.First>
            <Pagination.Prev onClick={() => table.previousPage()} disabled={!table.getCanPreviousPage()}>
              <span className="d-none d-md-inline-block">Previous</span>
              <span className="d-inline-block d-md-none">&lt;</span>
            </Pagination.Prev>

            <Pagination.Next onClick={() => table.nextPage()} disabled={!table.getCanNextPage()}>
              <span className="d-none d-md-inline-block">Next</span>
              <span className="d-inline-block d-md-none">&gt;</span>
            </Pagination.Next>

            <Pagination.Last onClick={() => table.setPageIndex(table.getPageCount() - 1)} disabled={!table.getCanNextPage()}>
              <span className="d-none d-md-inline-block">Last</span>
              <span className="d-inline-block d-md-none">&gt;&gt;</span>
            </Pagination.Last>
          </Pagination>
        </div>
      </div>
    </>
  );
}
